import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LifeBuoy, Loader2 } from "lucide-react";

export const HelpRequestButton = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [loading, setLoading] = useState(false);

    if (!user) return null;
    if (user.role === "support" || user.role === "admin") return null; // staff don't need this

    const submit = async () => {
        if (msg.trim().length < 3) {
            toast.error("Tell us a bit more so we can help.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/help/request", { message: msg.trim() });
            toast.success("Help request sent! A supporter will DM you shortly.");
            setOpen(false);
            setMsg("");
        } catch (e) {
            toast.error("Failed to send. Try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-40 inline-flex items-center gap-2 px-4 h-12 rounded-full text-white font-semibold shadow-lg"
                    style={{ background: "linear-gradient(135deg, #22d3ee 0%, #6f3fec 100%)", boxShadow: "0 8px 32px rgba(34,211,238,0.35), 0 0 0 1px rgba(255,255,255,0.08)" }}
                    data-testid="help-request-button"
                >
                    <LifeBuoy className="w-4 h-4" />
                    <span className="hidden sm:inline">Need Help?</span>
                    <span className="sm:hidden">Help</span>
                </button>
            </DialogTrigger>
            <DialogContent className="pp-bg-1 border-[#242436] text-white">
                <DialogHeader>
                    <DialogTitle className="font-display tracking-wider text-2xl">REQUEST SUPPORT</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-[#b7b7c6]">Drop a message and a supporter will DM you in <Link to="/messages" className="text-[#22d3ee] underline">Messages</Link>.</p>
                <Textarea
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    rows={4}
                    placeholder="What do you need help with?"
                    className="pp-bg-0 border-[#242436] text-white"
                    data-testid="help-request-textarea"
                />
                <DialogFooter>
                    <Button onClick={submit} disabled={loading} className="bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold" data-testid="help-request-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
